import { endpoint } from "@api/api";
import { useMutation } from "@tanstack/react-query";
import { CreateProjectForm } from "@pages/create-project/create-project";
import { PROJECT_CONSTANTS } from "@api/projects/constants";
import { numberToZeroIfEmpty, stringToNullIfEmpty } from "@components/create-project/table/helpers";

export interface CalculationRequestData {
  heated_area: number | "";
  heating_performance_total: number | "";
  heating_consumption_total: number | "";
  building_year: number | "";
  hwb_from_ea_as_planned: number | "";
  type_of_heating_distribution_w: string;
  type_of_warmwater_distribution_ww: string;
  type_of_warmwater_generation: string | null;
  spec_heating_consumption: number;
  spec_warm_water_consumption_central: number;
}

export interface CalculationResponse {
  data: {
    heating_power: number;
    heating_consumption: number;
    warm_water_power: number;
    warm_water_consumption: number;
    temperature_conditioning_power: number;
    temperature_conditioning_consumption: number;
    heating_performance_total: number | "";
    heating_consumption_total: number | "";
    spec_heating_power: number;
    spec_heating_consumption: number;
    spec_warm_water_power: number;
    spec_warm_water_consumption: number;
    spec_temperature_conditioning_power: number;
    spec_temperature_conditioning_consumption: number;
    spec_heating_performance_total: number;
    spec_heating_consumption_total: number;
  },
  checks: {
    ea_data: boolean;
    specific_entries_consumption: boolean;
    specific_entries_power: boolean;
  }
}

export interface ProjectResponse {
  id: string;
  postal_code: string;
  project_name: string;
  customer_name: string;
  created_at: string;
  inputs: {
    heated_area: number;
    heating_power: number;
    heating_consumption: number;
    warm_water_power: number;
    warm_water_consumption: number;
    temperature_conditioning_power: number;
    temperature_conditioning_consumption: number;
    heating_performance_total: number;
    heating_consumption_total: number;
    spec_heating_power: number;
    spec_heating_consumption: number;
    spec_warm_water_power: number;
    spec_warm_water_consumption_central: number;
    spec_temperature_conditioning_power: number;
    spec_temperature_conditioning_consumption: number;
    spec_heating_performance_total: number;
    spec_heating_consumption_total: number;
    optimization_for_pv: number;
    with_thermal_renovation: boolean;
    exterior_area: number;
    roof_area: number;
    modultyp: string;
    orientation: string;
    inclination_of_roof: string;
    energy_price_consumer_heat_and_ww_net: number;
    energy_price_consumer_temp_net: number;
    subsidy_public_grant_on_tic: number;
    electricity_price_index_vs_cpi: number;
  };
  outputs: {
    Building: {
      "Project Name": string;
      "Customer": string;
      "Heated Area": number;
      "Thermal Renovation": boolean;
      "Infrastructure": string;
      "Exterior Area": number;
      "PV Area Total": number;
      "PV performance": number;
    };
    Tech: {
      HWB: number;
      "HWB spec": number;
      WWB: number;
      "WWB spec": number;
      TEMPB: number;
      "TEMPB spec": number;
      Total: number;
      "Type Heating Distribution": string;
      "Type of Warmwater Distribution": string;
    };
    "Financials (General)": {
      "Effective Interest Rate": number;
      "Indexation": number;
      "Subsidy risk taken by": string;
      "Billing Type": string;
      "External Billing Support": string;
      "External Operations Mgmt": string;
    },
    "Offer internal information": {
      "Technical Calculation": {
        "Case 1": TcDetails;
        "Case 2": TcDetails;
        "Case 3": TcDetails;
      };
      "Financial Calculation": {
        "Case 1": FcDetails;
        "Case 2": FcDetails;
        "Case 3": FcDetails;
      };
      Financials: {
        "Case 1": FinancialsDetails1;
        "Case 2": FinancialsDetails2;
        "Case 3": FinancialsDetails3;
      };
    };
    "Offer to customer": {
      "Financial Information to Building Owner": {
        "Case 1": OfferToCustomer;
        "Case 2": OfferToCustomer;
        "Case 3": OfferToCustomer;
        "Comparison 1": OfferToCustomer;
      },
      "Life-Cycle-Cost": {
        "Ground-source": number[];
        "Air-source": number[];
        "Hybrid": number[];
        "DH": number[];
      }
    }
  };
}

export interface TcDetails {
  "Annual Performance Factor (JAZ)": number;
  "Electrical power HP": number | string;
  "Electrical power AHEx": number | string;
  "Electrical power AHP": number | string;
  "PV used roof area": number;
  "PV performance": number;
  "Probes length total": number | string;
  "Probes field area": number | string;
  "CHECK Exterior area vs. Probes field area": string;
  "Total Power requirement": number;
  "PV yield total": number;
  "Total electricity demand from grid": number;
  "CHECK Withdrawl Power in Probes": number;
}

export interface FcDetails {
  TIC: number;
  "TIC specific": number;
  "TIC specific (only HVAC)": number;
  "Subsidy/public grant (assumed)": number;
  "Credit/Loan": number;
  "TIC contribution by customer (BKZ)": number;
  "TIC contribution (BKZ) per m2": number;
  "TIC contribution (BKZ) per WE": number;
  "Average energy price total (HEAT+WW+TEMP)": number;
  "Average energy price specific p.a. (HEAT+WW+TEMP)": number;
  "Average energy price specific p.m. (HEAT+WW+TEMP)": number;
  "Total cost on 20y horizon": number;
  "End of Offer to Customer": number[];
  "TIC Building": number;
  "TIC Tech + Earthworks": number;
  "TIC Network Infrastructure": number;
  "TIC Mgmt + Contingencies": number;
  "Subsidy/Public Grant (assumed)": number;
  "Bank Loan": number;
  "BKZ (TIC contribution by customer)": number;
  "BKZ specific": number;
  "BKZ per unit": number;
  "BKZ-to-TIC Ratio": number;
}

export interface FinancialsDetails1 {
  TIC: number;
  "Subsidy/public grant": number;
  "GC margin": number;
  "Total Cost to customer": number;
  "TCC p.m2": number;
  "TCC p.WE": number;
  "Average OPEX total (HEAT+WW+TEMP)": number;
  "Average OPEX specific p.a. (HEAT+WW+TEMP)": number;
  "Average OPEX specific p.m. (HEAT+WW+TEMP)": number;
  "Total cost on 20y horizon": number;
  "DSCR": number;
  "Loan Term": number;
  "Free Cashflow Margin": number;
  "NPV": number;
  "IRR": number;
  "Ground-source": number[];
}

export interface FinancialsDetails2 extends Omit<FinancialsDetails1, "Ground-source"> {
  "Air-source": number[];
}

export interface FinancialsDetails3 extends Omit<FinancialsDetails1, "Ground-source"> {
  "Hybrid": number[];
}

export interface OfferToCustomer {
  "GP (W+WW)": number;
  "AP (W+WW)": number;
  "GP (TEMP)": number;
  "MP": number;
  "Summe p.a.": number;
  "Total Price rel.": number;
  "GP (W+WW) per month": number;
  "AP (W+WW) per month": number;
  "GP (TEMP) per month": number;
  "MP per month": number;
  "Summe p.m.": number;
  "BKZ": number;
  "BKZ rel.": number;
  "GP (W+WW) per m2": number;
  "AP (W+WW) per m2": number;
  "GP (TEMP) per m2": number;
  "MP per m2": number;
  "Summe p.a. per m2": number;
  "GP (W+WW) per m2 per month": number;
  "AP (W+WW) per m2 per month": number;
  "GP (TEMP) per m2 per month": number;
  "MP per m2 per month": number;
  "Summe p.m. per m2": number;
  "AP (W+WW) per MWh": number;
}

// Mappers
export const mapDTOToValues = (data: CalculationResponse, currentValues: CreateProjectForm) => {
  return {
    ...currentValues,
    heating_power: data.data.heating_power,
    heating_consumption: data.data.heating_consumption,
    warm_water_power: data.data.warm_water_power,
    warm_water_consumption: data.data.warm_water_consumption,
    temperature_conditioning_power: data.data.temperature_conditioning_power,
    temperature_conditioning_consumption: data.data.temperature_conditioning_consumption,
    heating_performance_total: data.data.heating_performance_total,
    heating_consumption_total: data.data.heating_consumption_total,
  };
};

export const mapValuesToOfferDTO = (values: CreateProjectForm) => {
  return {
    postal_code: values.postal_code === "" ? 0 : values.postal_code,
    project_name: values.project_name,
    customer_name: values.customer_name,
    project_description: values.project_description,
    general_comment: values.general_comment,
    inputs: {
      input_building: {
        number_of_units: values.number_of_units,
        building_current_status: values.building_current_status === "" ? PROJECT_CONSTANTS.DEFAULT_BUILDING_CURRENT_STATUS : values.building_current_status,
        type_of_infrastructure: values.type_of_infrastructure,
        "building_renovation_scheduled": true, // TODO: Replace with actual data later
        exterior_area: values.exterior_area,
        optimization_for_pv: PROJECT_CONSTANTS.DEFAULT_OPTIMIZATION_FOR_PV,
      },
      input_tech: {
        heated_area: values.heated_area,
        building_year: values.building_year,
        hwb_from_ea_as_planned: values.hwb_from_ea_as_planned || PROJECT_CONSTANTS.DEFAULT_HWB_FROM_EA_AS_PLANNED,
        type_of_heating_distribution_w: values.type_of_heating_distribution_w,
        type_of_warmwater_distribution_ww: values.type_of_warmwater_distribution_ww,
        type_of_warmwater_generation: values.type_of_warmwater_generation === "" ? null : values.type_of_warmwater_generation,
        with_thermal_renovation: values.with_thermal_renovation,
        heating_performance_total: values.heating_performance_total,
        heating_consumption_total: values.heating_consumption_total,
        "spec_heating_consumption": 48, // TODO: Replace with actual data later
        "spec_warm_water_consumption_central": 30, // TODO: Replace with actual data later
      },
      input_fin: {
        price_w_ww: {
          case_1: numberToZeroIfEmpty(values.price_w_ww),
          case_2: numberToZeroIfEmpty(values.price_w_ww),
          case_3: numberToZeroIfEmpty(values.price_w_ww),
        },
        price_temperature_conditioning: {
          case_1: numberToZeroIfEmpty(values.price_temperature_conditioning),
          case_2: numberToZeroIfEmpty(values.price_temperature_conditioning),
          case_3: numberToZeroIfEmpty(values.price_temperature_conditioning),
        },
        ratio_gp_w_ww_vs_gp_temp: {
          case_1: numberToZeroIfEmpty(values.ratio_gp_w_ww_vs_gp_temp),
          case_2: numberToZeroIfEmpty(values.ratio_gp_w_ww_vs_gp_temp),
          case_3: numberToZeroIfEmpty(values.ratio_gp_w_ww_vs_gp_temp),
        },
        dscr: {
          case_1: numberToZeroIfEmpty(values.dscr),
          case_2: numberToZeroIfEmpty(values.dscr),
          case_3: numberToZeroIfEmpty(values.dscr),
        },
        effective_interest_rate: numberToZeroIfEmpty(values.effective_interest_rate),
        loan_term: {
          case_1: numberToZeroIfEmpty(values.loan_term),
          case_2: numberToZeroIfEmpty(values.loan_term),
          case_3: numberToZeroIfEmpty(values.loan_term),
        },
        subsidy_risk_taken_by: stringToNullIfEmpty(values.subsidy_risk_taken_by),
        assumption_on_subsidy_rate: numberToZeroIfEmpty(values.assumption_on_subsidy_rate),
        assumption_on_subsidy_payment_date: numberToZeroIfEmpty(values.assumption_on_subsidy_payment_date),
        "indexation": 0, // TODO: add this field to UI
        billing_type: stringToNullIfEmpty(values.billing_type),
        external_billing_support: stringToNullIfEmpty(values.external_billing_support),
        external_operations_management: stringToNullIfEmpty(values.external_operations_management),
      },
      tech_calc: {
        pv_basic_power_and_module_determination: values.pv_basic_power_and_module_determination.map(roof => ({
          roof_area: roof.roof_area === "" ? PROJECT_CONSTANTS.DEFAULT_ROOF_AREA : roof.roof_area,
          useable_roof_area: roof.useable_roof_area === "" ? PROJECT_CONSTANTS.DEFAULT_USEABLE_ROOF_AREA : roof.useable_roof_area,
          orientation: roof.orientation === "" ? PROJECT_CONSTANTS.DEFAULT_ORIENTATION : roof.orientation,
          inclination_of_roof: roof.inclination_of_roof === "" ? PROJECT_CONSTANTS.DEFAULT_INCLINATION_OF_ROOF : roof.inclination_of_roof,
        }))
      },
      pricing_assumptions: {
        energy_price_consumer_heat_and_ww_net: values.energy_price_consumer_heat_and_ww_net,
        energy_price_consumer_temp_net: values.energy_price_consumer_temp_net,
        subsidy_public_grant_on_tic: values.subsidy_public_grant_on_tic,
        electricity_price_index_vs_cpi: values.electricity_price_index_vs_cpi,
      },
    }
  };
};

// Mutations
const sendCalculationRequest = async (data: CalculationRequestData) => {
  const response = await endpoint.post(
    "performance_and_consumption_of_building/",
    data
  );

  return response.data;
};

const offerCalculationMutation = async (data: CreateProjectForm) => {
  const response = await endpoint.post("calculate_offers/", mapValuesToOfferDTO(data));
  return response.data;
};

const createProject = async (project: ProjectResponse) => {
  const response = await endpoint.post("project/", project);
  return response.data;
};

// Hooks
export const useCalculationMutation = () => {
  return useMutation({
    mutationFn: (data: CalculationRequestData) => sendCalculationRequest(data),
  });
};

export const useOfferCalculationMutation = () => {
  return useMutation({
    mutationFn: (data: CreateProjectForm) => offerCalculationMutation(data)
  });
};

export const useCreateProjectMutation = () => {
  return useMutation({
    mutationFn: (data: ProjectResponse) => createProject(data)
  });
};
